import { IconButton } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { OptionsContextActionParameterProperties } from "../../../TrueUI/Tables/BaseTable2/TableProperties";
import { useSetRecoilState } from "recoil";
import { RefreshInsuredPageAtom } from "../../InsuredAtoms";
import { usePermissions } from "../../../../hooks";
import { PermissionsEnums } from "../../../../dtos/permissions-enums";
import { BaseGridProperties } from "../../../BaseGrid/BaseGridProperties";
import { useBaseGrid } from "../../../BaseGrid/Hooks/useBaseGrid";
import BaseGrid from "../../../BaseGrid/BaseGrid";
import Launch from "@mui/icons-material/Launch";
import BaseGridDropDownFilter from "../../../BaseGrid/BaseGridCommonFilters/BaseGridDropDownFilter";
import { StatusEnums } from "../../../../dtos/status-enums";
import { selectOptionsByEnumValues } from "../../../../utilities/enumFunctions";
import { ModalConfigurationOfficerProps } from "./TypesInsuredOfficers";
import ModalWrapperOfficers from "./ModalWrapperOfficers";

const tableName = "insuredInformationOfficers";

const INSURED_INFORMATION_API_PATH = "api/InsuredInformation";

type InsuredInformationOfficersProps = {
  insuredId: number;
};

const InsuredInformationOfficers: FC<InsuredInformationOfficersProps> = ({
  insuredId,
}) => {
  const { hasPermission } = usePermissions([
    PermissionsEnums.UNDERWRITING_QUOTE,
    PermissionsEnums.UNDERWRITTER,
    PermissionsEnums.PROGRAM_ADMIN,
  ]);
  const setRefreshPage = useSetRecoilState(RefreshInsuredPageAtom);

  const [modalConfiguration, setModalConfiguration] =
    useState<ModalConfigurationOfficerProps>({
      isOpen: false,
      refreshOfficerComponents: false,
      officerId: null,
      insuredId: insuredId,
    });

  const actionsForRows = (
    actionOptions: OptionsContextActionParameterProperties<any>
  ) => {
    return (
      <>
        {hasPermission && (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              setModalConfiguration({
                ...modalConfiguration,
                isOpen: true,
                officerId: actionOptions.row.OfficerId,
              });
            }}
          >
            <Launch fontSize="inherit" />
          </IconButton>
        )}
      </>
    );
  };

  const tableConfiguration: BaseGridProperties = {
    name: tableName,
    getURL: `${INSURED_INFORMATION_API_PATH}/GetInsuredInformationOfficers?insuredId=${insuredId}`,
    useManuallyReloadParameters: true,
    filterOptions: [
      (options) =>
        BaseGridDropDownFilter({
          id: "officers-status-filter",
          columnNames: ["OfficerStatus"],
          labelText: "Status:",
          optionsOverride: selectOptionsByEnumValues(StatusEnums).filter(
            (o) => o.intValue !== StatusEnums.DELETED
          ),
          options,
          defaultValue: StatusEnums.ACTIVE.toString(),
        }),
    ],
    columnOptions: [
      { fieldName: "OfficerType", width: 10 },
      { fieldName: "OfficerName", width: 19 },
      { fieldName: "OfficerTitle", width: 14 },
      { fieldName: "OwnershipPct", width: 8 },
      { fieldName: "ClassCode", width: 8 },
      { fieldName: "OfficerInclExcl", width: 9 },
      { fieldName: "EffectiveDate", width: 10 },
      { fieldName: "ExpirationDate", width: 10 },
      { fieldName: "OfficerStatus", width: 9 },
      { fieldName: "OPTIONS", width: 3 },
    ],
    advancedOptions: {
      overrideColumnDefaultValues: [
        {
          columnFieldName: "InsuredId",
          propName: "defaultValue",
          value: insuredId,
        },
      ],
      optionsContextActions: {
        contextComponent: actionsForRows,
      },
      optionsColumnConfiguration: {
        isHidden: false,
        optionType: "component",
      },
    },
    toolbarOptions: {
      showSortFilter: false,
      showImportButton: false,
      addButtonIsDisabled: !hasPermission,
      showEditButton: false,
    },
    events: {
      addEventOverrideCallback: () => {
        setModalConfiguration({
          ...modalConfiguration,
          isOpen: true,
          officerId: 0,
        });
      },
    },
  };

  const { manuallyReloadParameters } = useBaseGrid(tableConfiguration);

  useEffect(() => {
    if (modalConfiguration?.refreshOfficerComponents === true) {
      setRefreshPage({ refreshTabs: true });
      manuallyReloadParameters();
      setModalConfiguration({
        ...modalConfiguration,
        refreshOfficerComponents: false,
      });
    }
  }, [modalConfiguration?.refreshOfficerComponents]);

  return (
    <>
      <BaseGrid name={tableName} />
      <ModalWrapperOfficers
        setModalConfiguration={setModalConfiguration}
        modalConfiguration={modalConfiguration}
      />
    </>
  );
};

export default InsuredInformationOfficers;
